<template>
  <van-popup :show="show" position="bottom" round>
    <div class="searchable-picker">
      <!-- 标题栏 -->
      <div class="picker-header">
        <div class="picker-title">{{ title }}</div>
        <van-icon name="cross" @click="handleCancel" class="close-icon" />
      </div>
      
      <!-- 搜索框 -->
      <div class="search-container">
        <van-search
          v-model="searchKeyword"
          placeholder="请输入搜索关键词"
          @search="handleSearch"
          @clear="handleClear"
          class="search-input"
        />
      </div>
      
      <!-- 选项列表 -->
      <div class="options-container">
        <div 
          v-for="(option, index) in filteredOptions" 
          :key="index"
          class="option-item"
          :class="{ 'selected': isSelected(option) }"
          @click="handleSelect(option)"
        >
          <span class="option-text">{{ getDisplayValue(option) }}</span>
          <van-icon v-if="isSelected(option)" name="success" class="selected-icon" />
        </div>
        
        <!-- 无搜索结果提示 -->
        <div v-if="filteredOptions.length === 0" class="no-result">
          <van-icon name="search" class="no-result-icon" />
          <span class="no-result-text">暂无搜索结果</span>
        </div>
      </div>
      
      <!-- 底部按钮 -->
      <div class="picker-footer">
        <van-button type="default" @click="handleCancel" class="cancel-btn">取消</van-button>
        <van-button type="primary" @click="handleConfirm" class="confirm-btn" :disabled="!selectedValue">确定</van-button>
      </div>
    </div>
  </van-popup>
</template>

<script setup>
import { ref, computed, watch, defineProps, defineEmits } from 'vue'

// Props
const props = defineProps({
  show: {
    type: Boolean,
    default: false
  },
  title: {
    type: String,
    default: '请选择'
  },
  options: {
    type: Array,
    default: () => []
  },
  value: {
    type: [String, Number, Object],
    default: ''
  },
  valueKey: {
    type: String,
    default: 'name'
  },
  displayKey: {
    type: String,
    default: 'name'
  }
})

// Emits
const emit = defineEmits(['update:show', 'confirm', 'cancel'])

// 响应式数据
const searchKeyword = ref('')
const selectedValue = ref(props.value)

// 监听 props.value 变化
watch(() => props.value, (newValue) => {
  selectedValue.value = newValue
}, { immediate: true })

// 监听 show 变化，重置搜索关键词
watch(() => props.show, (newValue) => {
  if (newValue) {
    searchKeyword.value = ''
  }
})

// 过滤后的选项
const filteredOptions = computed(() => {
  if (!searchKeyword.value) {
    return props.options
  }
  return props.options.filter(option => {
    const displayValue = typeof option === 'object' ? option[props.displayKey] : String(option)
    return String(displayValue).toLowerCase().includes(searchKeyword.value.toLowerCase())
  })
})

// 方法
const handleSearch = () => {
  // 搜索逻辑已在 computed 中处理
}

const handleClear = () => {
  searchKeyword.value = ''
}

const handleSelect = (option) => {
  selectedValue.value = option
}

// 获取显示值
const getDisplayValue = (option) => {
  if (!option) {
    return ''
  }
  return typeof option === 'object' ? option[props.displayKey] : String(option)
}

// 判断是否选中
const isSelected = (option) => {
  if (!selectedValue.value) {
    return false
  }
  if (typeof option === 'object' && typeof selectedValue.value === 'object') {
    return option[props.valueKey] === selectedValue.value[props.valueKey]
  }
  return selectedValue.value === option
}

const handleConfirm = () => {
  emit('confirm', selectedValue.value)
  emit('update:show', false)
}

const handleCancel = () => {
  emit('cancel')
  emit('update:show', false)
}
</script>

<style scoped>
.searchable-picker {
  background: #fff;
  border-radius: 12px 12px 0 0;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
}

.picker-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid #f5f5f5;
}

.picker-title {
  font-size: 16px;
  font-weight: 500;
  color: #333;
}

.close-icon {
  font-size: 20px;
  color: #999;
  cursor: pointer;
}

.search-container {
  padding: 12px 20px;
  border-bottom: 1px solid #f5f5f5;
}

.search-input {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 0;
}

:deep(.search-input .van-search__content) {
  background: transparent;
}

:deep(.search-input .van-field__control) {
  background: transparent;
}

.options-container {
  flex: 1;
  overflow-y: auto;
  max-height: 50vh;
}

.option-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid #f5f5f5;
  cursor: pointer;
  transition: background-color 0.2s;
}

.option-item:hover {
  background-color: #f8f9fa;
}

.option-item.selected {
  background-color: #f0f9ff;
  color: var(--van-primary-color);
}

.option-text {
  font-size: 14px;
  color: #333;
}

.option-item.selected .option-text {
  color: var(--van-primary-color);
  font-weight: 500;
}

.selected-icon {
  color: var(--van-primary-color);
  font-size: 16px;
}

.no-result {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  color: #999;
}

.no-result-icon {
  font-size: 48px;
  margin-bottom: 12px;
  color: #ddd;
}

.no-result-text {
  font-size: 14px;
  color: #999;
}

.picker-footer {
  display: flex;
  gap: 12px;
  padding: 16px 20px;
  border-top: 1px solid #f5f5f5;
}

.cancel-btn {
  flex: 1;
  background: #f5f5f5;
  border: none;
  color: #666;
  border-radius: 8px;
  height: 44px;
  font-size: 16px;
}

.confirm-btn {
  flex: 1;
  background: var(--van-primary-color);
  border: none;
  color: #fff;
  border-radius: 8px;
  height: 44px;
  font-size: 16px;
}

/* 滚动条样式 */
.options-container::-webkit-scrollbar {
  width: 4px;
}

.options-container::-webkit-scrollbar-track {
  background: #f1f1f1;
}

.options-container::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 2px;
}

.options-container::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}
</style> 